/** @format */

// Single source for the Off Cut Shades Fit Guarantee. Every placement
// (homepage, product builder, cart/checkout, guarantee page, measuring guide,
// footer, FAQ) reads its copy, terms and links from here, so a change to the
// policy is made once.

export const FIT_GUARANTEE_ANCHOR = "fit-guarantee";
export const FIT_GUARANTEE_REASON = "fit-guarantee";

export const fitGuaranteeLinks = {
	details: `/help/return-policy#${FIT_GUARANTEE_ANCHOR}`,
	measuringGuide: "/guides/measuring-guide",
	contact: `/contact?reason=${FIT_GUARANTEE_REASON}#contact-form`,
};

export const fitGuarantee = {
	name: "Fit Guarantee",
	tagline: "Made to Measure. Made to Fit.",
	signature:
		"The Off Cut Shades Fit Guarantee. Included with every eligible order.",
	days: 30,

	// 1. Homepage value proposition
	home: {
		title: "Made to Measure. Made to Fit.",
		text: "Every shade is made specifically for your windows. And if your measurements aren't quite right, we'll help make it right with our complimentary Fit Guarantee.",
		linkText: "Explore Our Fit Guarantee",
	},

	// 2. Product builder (size step and final summary)
	builder: {
		title: "Made to fit your space.",
		text: "Not completely confident in your measurements? Every shade includes our complimentary Fit Guarantee, with qualifying size corrections covered for 30 days after delivery.",
		links: [
			{ text: "Measuring Guide", to: fitGuaranteeLinks.measuringGuide },
			{ text: "Fit Guarantee Details", to: fitGuaranteeLinks.details },
		],
	},

	// 3. Cart and checkout
	cart: {
		title: "Your fit is covered.",
		text: "Every custom shade includes our 30-day Fit Guarantee. If you've made a measuring mistake, we're here to help make it right.",
		linkText: "View Guarantee",
	},

	// 4. Guarantee page (/help/return-policy#fit-guarantee)
	page: {
		title: "Made to Measure. Made to Fit.",
		subtitle: "Beautifully crafted shades, tailored to your windows.",
		intro: [
			"Every Off Cut Shades order includes our Fit Guarantee, giving you confidence from the moment you measure to the moment your shades are installed.",
			"If your measurements aren't quite right, we'll work with you to correct the fit, including a complimentary adjustment or remake when needed.",
		],
		included: "Included with every eligible order. No additional cost.",
		steps: [
			{
				title: "Let Us Know",
				text: "Contact us within 30 days of delivery with your order number, a photo of your window and the measurements that need correcting.",
			},
			{
				title: "We'll Review the Fit",
				text: "Our team will help confirm the correct measurements and determine the best solution, whether that means adjusting your existing shade or making a replacement.",
			},
			{
				title: "Made Right for Your Space",
				text: "We'll make the necessary correction or remake your eligible shade and arrange delivery, so you can enjoy the finished result.",
			},
		],
		terms: [
			"Requests must be submitted within 30 days of delivery.",
			"One complimentary adjustment or remake per eligible shade, up to four shades per household.",
			"The guarantee applies to measurement errors on eligible residential orders placed directly through Off Cut Shades.",
			"Replacements must retain the original product style, fabric, colour and operating system.",
			"If the corrected size costs more than the original, the customer only pays the price difference.",
			"Standard shipping of approved replacements within Canada is complimentary.",
			"The guarantee covers fit corrections, not changes in style, colour or personal preference.",
			"Installation errors, modified products, wholesale and commercial orders are excluded.",
			"Manufacturing defects and errors made by Off Cut Shades are handled separately under our quality guarantee and applicable warranty.",
		],
		ctaTitle: "Need Help With Your Fit?",
		ctaText: "Contact Us",
	},

	// 5. Measuring guide
	measuring: {
		title: "Measure with Confidence.",
		text: "A precise fit starts with the right measurements. Follow our simple guide and we'll take care of the manufacturing details, including the appropriate deductions for your selected mounting style.",
		reassurance: "And if you make a measuring mistake, our Fit Guarantee is here to help.",
		linkText: "Learn About Our Fit Guarantee",
	},

	// 6. FAQ (rendered in the FAQ's "Warranty & Returns" group)
	faq: [
		{
			id: "fit-measure-incorrectly",
			question: "What happens if I measure my windows incorrectly?",
			answer:
				"Every eligible Off Cut Shades order includes our Fit Guarantee. If your measurements aren't quite right, contact us within 30 days of delivery. We'll review the measurements with you and arrange a complimentary adjustment or remake when eligible.",
		},
		{
			id: "fit-cost-extra",
			question: "Does the Fit Guarantee cost extra?",
			answer:
				"No. Our Fit Guarantee is included with every eligible residential order at no additional cost.",
		},
		{
			id: "fit-change-style",
			question:
				"Can I change the colour or style of my shade under the Fit Guarantee?",
			answer:
				"The Fit Guarantee is designed to correct measurement errors. Changes to fabric, colour, style or operating system aren't included.",
		},
	],
};

export default fitGuarantee;
